import { McoStaySuggestionItem, McoStaySuggestionWildcardChild } from '@/misc/types'
import { Suggestion } from '@/stay-displayer/types'

// Suggestions with a rejected (or uncertain) evaluation go below the other ones, each group keeping its order.
export function sortRejectedSuggestionsLast(suggestions: Suggestion[]): Suggestion[] {
  return [
    ...suggestions.filter((suggestion) => !suggestion.hasRejectedEvaluation),
    ...suggestions.filter((suggestion) => suggestion.hasRejectedEvaluation),
  ]
}

// A separator goes right before the first rejected suggestion, only when other suggestions are displayed above it.
export function isFirstRejectedSuggestion(suggestions: Suggestion[], index: number): boolean {
  return index > 0 && suggestions[index].hasRejectedEvaluation && !suggestions[index - 1].hasRejectedEvaluation
}

// A code is rejected on the displayed RUM when an evaluation of that RUM or of the whole stay (null) rejects it, like
// the evaluations kept in the justification popin (see keepRumEvaluations). The RSS view (rumId 0) gathers every RUM.
export function isRejectedOnRum(rejectedEvaluationRumIds: (number | null)[], rumId: number): boolean {
  if (rumId === 0) {
    return rejectedEvaluationRumIds.length > 0
  }
  return rejectedEvaluationRumIds.some((rejectedRumId) => rejectedRumId === null || rejectedRumId === rumId)
}

// A wildcard parent is never evaluated, only its children are: a wildcard with rejected children is split so that
// they go below with the other rejected suggestions, the other children staying in the wildcard above.
// A part left with a single child is shown as that child, without its wildcard parent.
export function splitWildcardsByEvaluation(items: McoStaySuggestionItem[], rumId: number): McoStaySuggestionItem[] {
  return items.flatMap((item) => {
    const isRejected = (child: McoStaySuggestionWildcardChild) => isRejectedOnRum(child.rejectedEvaluationRumIds, rumId)
    const rejectedChildren = item.wildcardChildren.filter(isRejected)
    if (!item.isWildcard || !rejectedChildren.length) {
      return [item]
    }
    const otherChildren = item.wildcardChildren.filter((child) => !isRejected(child))

    return [
      ...(otherChildren.length ? [wildcardPart(item, otherChildren, [])] : []),
      wildcardPart(item, rejectedChildren, [...new Set(rejectedChildren.flatMap((child) => child.rejectedEvaluationRumIds))]),
    ]
  })
}

// The part keeps the RUMs its children are rejected on, so that it is flagged like them
function wildcardPart(item: McoStaySuggestionItem, children: McoStaySuggestionWildcardChild[], rejectedEvaluationRumIds: (number | null)[]): McoStaySuggestionItem {
  if (children.length > 1) {
    return { ...item, wildcardChildren: children, rejectedEvaluationRumIds }
  }
  const [child] = children
  // A child is suggested by the searches of its wildcard parent too (see DiagnosisListStandardMode's buildSuggestion)
  return { ...item, ...child, searches: [...item.searches, ...child.searches], isWildcard: false, wildcardChildren: [] }
}

// Both parts of a split wildcard share its reference
export function suggestionKey(suggestion: Suggestion): string {
  return suggestion.hasRejectedEvaluation ? `${suggestion.reference}:rejected` : suggestion.reference
}

// A list with at least one rejected suggestion hides them until asked, along with the suggestions that are not always
// visible (e.g. the non-revaluating ones), behind the button about the non-relevant suggestions.
export function hasRejectedSuggestion(suggestions: Suggestion[]): boolean {
  return suggestions.some((suggestion) => suggestion.hasRejectedEvaluation)
}

export function isSuggestionDisplayed(suggestion: Suggestion, showMore: boolean): boolean {
  return showMore || (suggestion.isAlwaysVisible && !suggestion.hasRejectedEvaluation)
}

export function showMoreLabelKey(hidesRejected: boolean, showMore: boolean): string {
  if (hidesRejected) {
    return showMore ? 'stayDisplayer.hideNonRelevantSuggestions' : 'stayDisplayer.showNonRelevantSuggestions'
  }

  return showMore ? 'stayDisplayer.showLessSuggestions' : 'stayDisplayer.showMoreSuggestions'
}
